import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../Icon/Icon';
import HtmlCreativePreview from './HtmlCreativePreview';

// Dynamic creatives have no size of their own: they play at the screen size.
const DEFAULT_SIZE = { width: 1920, height: 1080 };

// Largest share of the viewport the preview may take, so it also fits on
// small (e.g. 1280x720) monitors.
const MAX_WIDTH_VW = 80;
const MAX_HEIGHT_VH = 65;

const TRANSLATIONS =
    'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card';

const getSize = (size) =>
    Number(size?.width) > 0 && Number(size?.height) > 0
        ? { width: Number(size.width), height: Number(size.height) }
        : DEFAULT_SIZE;

// Explicit width and height: with aspect-ratio alone the box grows to the
// unscaled content (a 1080px tall iframe) and overflows the screen.
const getBoxStyle = ({ width, height }) => {
    const ratio = width / height;

    return {
        width: `min(${MAX_WIDTH_VW}vw, ${MAX_HEIGHT_VH * ratio}vh, ${width}px)`,
        height: `min(${MAX_WIDTH_VW / ratio}vw, ${MAX_HEIGHT_VH}vh, ${height}px)`,
    };
};

const PreviewContent = ({ type, url, title, size, fit }) => {
    if (type === 'html') {
        return (
            <HtmlCreativePreview
                url={url}
                title={title}
                width={size.width}
                height={size.height}
                fit={fit}
            />
        );
    }

    const mediaStyle = fit
        ? { width: '100%', height: '100%', objectFit: 'contain' }
        : { width: size.width, height: size.height, maxWidth: 'none' };
    const media =
        type === 'video' ? (
            <video
                src={url}
                controls
                autoPlay
                muted
                className="block"
                style={mediaStyle}
            />
        ) : (
            <img src={url} alt={title} className="block" style={mediaStyle} />
        );

    return fit ? media : <div className="h-full w-full overflow-auto">{media}</div>;
};

// Previews open on demand, one at a time: a dynamic creative is a full web
// page (scripts, requests, animations) and closing the modal unloads it.
const CreativePreviewModal = ({ type, url, title, size, onClose }) => {
    const { t } = useTranslation();
    const [fit, setFit] = useState(true);
    const previewSize = getSize(size);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
            data-testid="CreativePreviewModal">
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="flex flex-col overflow-hidden rounded-xl bg-white text-sm text-gray-700 shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <span className="truncate font-medium text-gray-800">
                        {title}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-black focus:outline-none"
                        aria-label={t('common.buttons.cancel')}>
                        <Icon name="times" fixedWidth />
                    </button>
                </div>
                <div
                    className="self-center overflow-hidden bg-gray-200"
                    style={getBoxStyle(previewSize)}
                    data-testid="CreativePreviewModal-box">
                    <PreviewContent
                        type={type}
                        url={url}
                        title={title}
                        size={previewSize}
                        fit={fit}
                    />
                </div>
                <div className="flex items-center justify-between gap-4 px-4 py-2 text-xs text-gray-500">
                    <span>
                        {previewSize.width} x {previewSize.height}
                    </span>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => setFit((current) => !current)}
                            className="font-medium text-blue-700 hover:underline focus:outline-none"
                            data-testid="CreativePreviewModal-toggle-fit">
                            {t(
                                `${TRANSLATIONS}.${
                                    fit ? 'preview-actual-size' : 'preview-fit'
                                }`
                            )}
                        </button>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700">
                            {t(`${TRANSLATIONS}.open-new-tab`)}
                        </a>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CreativePreviewModal;
